/**
 * Streaming Events Utilities
 * 
 * Custom event system for communicating LLM streaming lifecycle events
 * between the chat frontend and AdMesh WeaveAdFormatContainer.
 * 
 * This allows WeaveAdFormatContainer to trigger final link detection
 * when streaming completes, rather than using arbitrary timeouts.
 */

import { logger } from './logger';

export const STREAMING_START_EVENT = 'admesh:streamingStart';
export const STREAMING_COMPLETE_EVENT = 'admesh:streamingComplete';

export interface StreamingStartEventDetail {
  messageId: string;
  sessionId: string;
  timestamp: number;
}

export interface StreamingCompleteEventDetail {
  messageId: string;
  sessionId: string;
  timestamp: number;
  metadata?: {
    hasRecommendations?: boolean;
    recommendationCount?: number;
  };
}

/**
 * Dispatch a streaming start event
 * 
 * Call this when the backend starts streaming the LLM response.
 * This signals to WeaveAdFormatContainer that content is being received.
 * 
 * @param messageId - Unique identifier for the message
 * @param sessionId - Session/chat identifier
 */
export function dispatchStreamingStartEvent(
  messageId: string,
  sessionId: string
): void {
  const detail: StreamingStartEventDetail = {
    messageId,
    sessionId,
    timestamp: Date.now(),
  };

  const event = new CustomEvent(STREAMING_START_EVENT, { detail });
  window.dispatchEvent(event);

  logger.log('[StreamingEvents] 📢 Dispatched streamingStart event', {
    messageId,
    sessionId
  });
}

/**
 * Dispatch a streaming complete event
 * 
 * Call this when the backend finishes streaming the LLM response.
 * This triggers WeaveAdFormatContainer to perform final link detection.
 * 
 * @param messageId - Unique identifier for the message
 * @param sessionId - Session/chat identifier
 * @param metadata - Optional metadata about recommendations
 */
export function dispatchStreamingCompleteEvent(
  messageId: string,
  sessionId: string,
  metadata?: {
    hasRecommendations?: boolean;
    recommendationCount?: number;
  }
): void {
  const detail: StreamingCompleteEventDetail = {
    messageId,
    sessionId,
    timestamp: Date.now(),
    metadata,
  };

  const event = new CustomEvent(STREAMING_COMPLETE_EVENT, { detail });
  window.dispatchEvent(event);

  logger.log('[StreamingEvents] 📢 Dispatched streamingComplete event', {
    messageId,
    sessionId,
    metadata
  });
}

/**
 * Listen for streaming start events
 * 
 * @param messageId - Filter events by this messageId
 * @param sessionId - Filter events by this sessionId
 * @param callback - Called when matching event is received
 * @returns Cleanup function to remove the event listener
 */
export function onStreamingStart(
  messageId: string,
  sessionId: string,
  callback: (detail: StreamingStartEventDetail) => void
): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<StreamingStartEventDetail>;
    
    // Only trigger callback if this is the message we're waiting for
    if (
      customEvent.detail.messageId === messageId &&
      customEvent.detail.sessionId === sessionId
    ) {
      logger.log('[StreamingEvents] 📨 Received streamingStart event');
      callback(customEvent.detail);
    }
  };

  window.addEventListener(STREAMING_START_EVENT, handler);

  // Return cleanup function
  return () => {
    window.removeEventListener(STREAMING_START_EVENT, handler);
  };
}

/**
 * Listen for streaming complete events
 * 
 * @param messageId - Filter events by this messageId
 * @param sessionId - Filter events by this sessionId
 * @param callback - Called when matching event is received
 * @returns Cleanup function to remove the event listener
 */
export function onStreamingComplete(
  messageId: string,
  sessionId: string,
  callback: (detail: StreamingCompleteEventDetail) => void
): () => void {
  logger.log('[StreamingEvents] 👂 Setting up streamingComplete listener', {
    expectedMessageId: messageId,
    expectedSessionId: sessionId
  });

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<StreamingCompleteEventDetail>;
    
    logger.log('[StreamingEvents] 📨 Received streamingComplete event (checking match)', {
      receivedMessageId: customEvent.detail.messageId,
      receivedSessionId: customEvent.detail.sessionId,
      expectedMessageId: messageId,
      expectedSessionId: sessionId,
      messageIdMatch: customEvent.detail.messageId === messageId,
      sessionIdMatch: customEvent.detail.sessionId === sessionId
    });
    
    // Only trigger callback if this is the message we're waiting for
    if (
      customEvent.detail.messageId === messageId &&
      customEvent.detail.sessionId === sessionId
    ) {
      logger.log('[StreamingEvents] ✅ Event matched! Calling callback');
      callback(customEvent.detail);
    } else {
      logger.log('[StreamingEvents] ⚠️ Event did not match - ignoring');
    }
  };

  window.addEventListener(STREAMING_COMPLETE_EVENT, handler);

  // Return cleanup function
  return () => {
    logger.log('[StreamingEvents] 🧹 Removing streamingComplete listener');
    window.removeEventListener(STREAMING_COMPLETE_EVENT, handler);
  };
}
